import { useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import { AlertTriangle } from 'lucide-react'
import { FormDialog, PermissionGate } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { useCompanySettings } from '@/hooks/useCompanySettings'
import { toApiProblem } from '@/lib/api/client'
import { problemMessage } from '@/lib/api/problem'
import { currencyOptions, currencySymbol, timezoneOptions } from '@/lib/locale-options'
import { useSettingsMutations } from '../api'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * The form's own shape, spelled out rather than a `Record<string, string>`.
 *
 * <p>`noUncheckedIndexedAccess` is on, so every lookup on an index signature is
 * `string | undefined` — which is correct, because nothing stops a key being absent. Naming
 * the eight fields says they are all present, which is true: `open()` fills every one from
 * the saved settings. The alternative was eight `?? ''` patches papering over a type that
 * was lying about the data.
 */
interface LocalisationDraft {
  defaultCurrency: string
  currencySymbol: string
  timezone: string
  dateFormat: string
  timeFormat: string
  weekStart: string
  fiscalYearStart: string
  defaultLocale: string
  primaryColor: string
}

/**
 * Localisation, and the first screen that can change it.
 *
 * <p>The endpoint has always accepted these values; nothing sent them. That is how a UAE
 * company ends up showing ৳ against dirham amounts: the schema's default symbol is the taka
 * from the original reference design, and until now there was no way to correct it. The
 * fallback was removed from `useCompanySettings` for exactly that reason — a wrong symbol on
 * a payslip is not cosmetic — but removing the fallback does not fix a wrong stored value.
 *
 * <p>Changing the currency refills the symbol from `Intl`, because the two disagreeing is the
 * fault being fixed. It stays editable afterwards: a company that writes "AED" rather than
 * "د.إ" is not wrong, and only they know which they want.
 */
export function LocalisationCard() {
  const { settings, unavailable } = useCompanySettings()
  const { saveLocalisation } = useSettingsMutations()

  const [draft, setDraft] = useState<LocalisationDraft | null>(null)
  const [error, setError] = useState<string | null>(null)

  const currencies = useMemo(() => currencyOptions(document.documentElement.lang || 'en'), [])
  const timezones = useMemo(() => timezoneOptions(), [])

  function open() {
    if (!settings) return
    setError(null)
    setDraft({
      defaultCurrency: settings.defaultCurrency,
      currencySymbol: settings.currencySymbol,
      timezone: settings.timezone,
      dateFormat: settings.dateFormat,
      timeFormat: settings.timeFormat,
      weekStart: String(settings.weekStart),
      fiscalYearStart: settings.fiscalYearStart,
      defaultLocale: settings.defaultLocale,
      primaryColor: settings.primaryColor,
    })
  }

  async function submit() {
    if (!draft) return
    setError(null)
    try {
      await saveLocalisation.mutateAsync({
        defaultCurrency: draft.defaultCurrency.trim().toUpperCase(),
        currencySymbol: draft.currencySymbol.trim(),
        timezone: draft.timezone.trim(),
        dateFormat: draft.dateFormat.trim(),
        timeFormat: draft.timeFormat.trim(),
        weekStart: Number(draft.weekStart),
        fiscalYearStart: draft.fiscalYearStart.trim(),
        defaultLocale: draft.defaultLocale.trim(),
        primaryColor: draft.primaryColor.trim(),
      })
      setDraft(null)
    } catch (caught) {
      setError(problemMessage(toApiProblem(caught) ?? caught, 'That could not be saved'))
    }
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">Localisation</h3>
        {!unavailable && (
          <PermissionGate permission="settings:write">
            <Button type="button" variant="ghost" size="sm" onClick={open}>
              <Pencil className="size-4" aria-hidden="true" />
              Edit
            </Button>
          </PermissionGate>
        )}
      </div>

      {unavailable
        ? (
          <p className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            The company's settings row could not be loaded, so nothing on any screen knows
            which currency to use. Money is shown without a symbol until it can.
          </p>
        )
        : (
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Currency</dt>
              <dd>{settings?.defaultCurrency} ({settings?.currencySymbol})</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Timezone</dt>
              <dd>{settings?.timezone}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Dates</dt>
              <dd>{settings?.dateFormat}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Week starts</dt>
              <dd>{DAYS[settings?.weekStart ?? 1]}</dd>
            </div>
          </dl>
        )}

      <p className="mt-3 text-xs text-[var(--color-muted)]">
        Every screen in the product formats against these. There is deliberately no fallback:
        when they cannot be loaded, money renders without a symbol rather than in a currency
        nobody chose.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(next) => { if (!next) { setDraft(null); setError(null) } }}
        title="Localisation"
        description="How this company writes money, dates and time. Every screen reads these."
        loading={saveLocalisation.isPending}
        error={error}
        onSubmit={() => void submit()}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="loc-currency">Currency</Label>
              <Select
                id="loc-currency"
                className="w-full"
                value={draft.defaultCurrency}
                onChange={(event) => setDraft({
                  ...draft,
                  defaultCurrency: event.target.value,
                  // Refilled, not forced: the two disagreeing is the fault being fixed, and
                  // the field below stays editable for a company that prefers the code.
                  currencySymbol: currencySymbol(event.target.value, document.documentElement.lang || 'en'),
                })}
              >
                {currencies.map((currency) => (
                  <option key={currency.value} value={currency.value}>{currency.label}</option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="loc-symbol">Currency symbol</Label>
              <Input
                id="loc-symbol"
                maxLength={8}
                value={draft.currencySymbol}
                onChange={(event) => setDraft({ ...draft, currencySymbol: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                What every amount in the product is prefixed with. Filled in from the currency
                when you change it; type over it to use something else, such as the code.
              </p>
            </div>

            <div>
              <Label htmlFor="loc-timezone">Timezone</Label>
              <Select
                id="loc-timezone"
                className="w-full"
                value={draft.timezone}
                onChange={(event) => setDraft({ ...draft, timezone: event.target.value })}
              >
                {timezones.map((zone) => (
                  <option key={zone.value} value={zone.value}>{zone.label}</option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="loc-week">Week starts</Label>
              <Select
                id="loc-week"
                className="w-full"
                value={draft.weekStart}
                onChange={(event) => setDraft({ ...draft, weekStart: event.target.value })}
              >
                {DAYS.map((day, index) => (
                  <option key={day} value={String(index)}>{day}</option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="loc-date">Date format</Label>
              <Input
                id="loc-date"
                value={draft.dateFormat}
                onChange={(event) => setDraft({ ...draft, dateFormat: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Java pattern letters, e.g. dd MMM yyyy.
              </p>
            </div>

            <div>
              <Label htmlFor="loc-time">Time format</Label>
              <Input
                id="loc-time"
                value={draft.timeFormat}
                onChange={(event) => setDraft({ ...draft, timeFormat: event.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="loc-fiscal">Fiscal year starts</Label>
              <Input
                id="loc-fiscal"
                value={draft.fiscalYearStart}
                placeholder="01-01"
                onChange={(event) => setDraft({ ...draft, fiscalYearStart: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">Month and day, as MM-DD.</p>
            </div>

            <div>
              <Label htmlFor="loc-locale">Default language</Label>
              <Select
                id="loc-locale"
                className="w-full"
                value={draft.defaultLocale}
                onChange={(event) => setDraft({ ...draft, defaultLocale: event.target.value })}
              >
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </Select>
            </div>
          </div>
        )}
      </FormDialog>
    </Card>
  )
}
